import React, { useState } from 'react';
import { useLocation } from '@reach/router';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Button from '@components/shared/Button';
import Text from '@components/shared/Text';
import TwoColumn from '@components/shared/TwoColumn';
import PadsCompareModal from '@components/storefront/PadsCompareModal';
import { PageSlice, SliceImageTextDetails, SliceImageTextItem } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { replaceText } from '@utils/handleTextReplacement';
import useNavigate from '@utils/useNavigate';
import Colors from './Colors';

interface Props {
  slice: PageSlice;
}

const ImageText: React.FC<Props> = ({ slice }) => {
  const { navigateWrapper } = useNavigate();
  const location = useLocation();
  const { country } = useCountry();
  const primary = slice.primary as SliceImageTextDetails;
  const image = getImage(primary?.image || null);
  const imageAlt = replaceText(primary?.image?.alt, country, location) ?? '';
  const { alignment, padding, mobile_padding } = primary;
  const layout = primary?.layout;
  const desktopReverse = layout === 'text-image';
  const mobileReverse = primary?.mobile_reverse;
  const gap = primary?.gap?.text;
  const breakpoint = primary?.breakpoint?.text || '1024px';
  const backgroundColor = primary?.background_color;
  const backgroundGradient = primary?.background_gradient?.text;
  const buttons = (slice.items ?? []) as SliceImageTextItem[];
  const textColor = primary?.text_color;
  const textHTML = primary?.text?.html;
  const imageWidth = primary?.image_width?.text || '400px';
  const mobileImageWidth = primary?.mobile_image_width?.text || '300px';
  const textWidth = primary?.text_width?.text || '476px';
  const border = primary?.border?.text;
  const [modalOpen, setModalOpen] = useState(false);
  const comparePadsPath = '/products/bamboo-menstrual-pads/compare';

  const handleNavigation = (buttonLinkText: string) => {
    if (buttonLinkText === comparePadsPath) {
      setModalOpen(true);
    } else if (buttonLinkText === 'scrollTop') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigateWrapper(buttonLinkText);
    }
  };

  return (
    <Container
      $backgroundColor={backgroundColor}
      $backgroundGradient={backgroundGradient}
      $breakpoint={breakpoint}
      $padding={padding?.text}
      $mobilePadding={mobile_padding?.text}
      $border={border}
      $mobileTextAlign={primary?.mobile_text_align}
    >
      <TwoColumn
        layout={layout}
        desktopReverse={desktopReverse}
        mobileReverse={mobileReverse}
        alignment={alignment}
        breakpoint={breakpoint}
        gap={gap}
        gapMobile={primary?.mobile_gap?.text}
      >
        <ImgWrap $breakpoint={breakpoint} $imageWidth={imageWidth} $mobileImageWidth={mobileImageWidth}>
          {image && <GatsbyImage image={image} alt={imageAlt} loading="lazy" />}
          {!image && primary?.video && (
            <video autoPlay={true} loop={true} muted={true} playsInline={true}>
              <source src={primary?.video?.url} type="video/mp4" />
            </video>
          )}
        </ImgWrap>
        <TextColumn $breakpoint={breakpoint} $textWidth={textWidth} $mobileTextAlign={primary?.mobile_text_align}>
          <Text text={textHTML} textAlign={primary?.text_align} textColor={textColor} />
          {buttons.map((button, index) => {
            if (!button?.button_text?.text || !button?.button_link?.text) return null;
            return (
              <ButtonStyled
                key={index}
                onClick={() => handleNavigation(button.button_link.text)}
                $textColor={button.button_text_color}
                $backgroundColor={button.button_color}
              >
                {button.button_text.text}
              </ButtonStyled>
            );
          })}
        </TextColumn>
      </TwoColumn>
      <PadsCompareModal modalOpen={modalOpen} closeModal={() => setModalOpen(false)} />
    </Container>
  );
};

export default ImageText;

interface ButtonProps {
  $backgroundColor?: string;
  $textColor?: string;
}

const Container = styled.div<{
  $border?: string;
  $backgroundColor?: string;
  $backgroundGradient?: string;
  $padding?: string;
  $breakpoint?: string;
  $mobilePadding?: string;
  $mobileTextAlign?: string;
}>`
  ${(props) => `border: ${props.$border || 'none'}`};
  margin-left: ${(props) => (props.$border ? '200px' : '0')};
  margin-right: ${(props) => (props.$border ? '200px' : '0')};
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(props) => props.$backgroundColor};
  background-image: ${(props) => (props.$backgroundGradient ? props.$backgroundGradient : 'none')};
  padding: ${(props) => (props.$padding ? props.$padding : '30px')};

  @media (max-width: ${(props) => props.$breakpoint}) {
    p,
    h2,
    h3,
    h4,
    h5,
    h6 {
      text-align: ${(props) => props.$mobileTextAlign || 'center'};
    }

    padding: ${(props) => props.$mobilePadding || props.$padding || '30px'};
  }

  @media (max-width: 768px) {
    border: none;
    margin-left: 0;
    margin-right: 0;
  }
`;

const TextColumn = styled.div<{ $textWidth?: string; $breakpoint?: string; $mobileTextAlign?: string }>`
  max-width: ${(props) => props.$textWidth};
  align-items: flex-start;
  display: flex;

  @media (max-width: ${(props) => props.$breakpoint}) {
    margin-top: 30px;

    align-items: ${(props) => (props.$mobileTextAlign === 'left' ? 'flex-start' : 'center')};
  }
`;

const ImgWrap = styled.div<{
  $breakpoint?: string;
  $imageWidth: string;
  $mobileImageWidth: string;
}>`
  max-width: ${(props) => props.$imageWidth};

  @media (max-width: ${(props) => props.$breakpoint}) {
    max-width: ${(props) => props.$mobileImageWidth};
    align-self: center;
  }
`;

const ButtonStyled = styled(Button)<{ $backgroundColor?: string; $textColor?: string }>`
  margin-top: 20px;
  color: ${({ $textColor }: ButtonProps) => $textColor || Colors.cream};
  background: ${({ $backgroundColor }: ButtonProps) => $backgroundColor || Colors.lilac};
`;
